/**
 * Trunnion AI - Execution Compare
 * Main comparison component. Reads `left` and `right` execution IDs from URL
 * search params, fetches both via React Query, and renders a two-column
 * side-by-side layout with headers and a diff view. Falls back to
 * CompareSelector when both IDs are not yet provided.
 */

import { useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, GitCompareArrows, ExternalLink } from 'lucide-react';
import { useExecution } from '@/hooks/queries/useExecutions';
import { CompareSelector } from './CompareSelector';
import { CompareDiffView } from './CompareDiffView';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ClassificationTag } from '@/components/ui/ClassificationTag';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { EXECUTION_STATUS_CONFIG } from '@/config/constants';
import { ROUTES as ROUTE_PATHS } from '@/config/routes';
import type { Execution, ExecutionStatus } from '@/types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Maps execution status to Badge variant for visual consistency. */
function statusVariant(
  status: ExecutionStatus,
): 'success' | 'error' | 'info' | 'warning' | 'default' {
  switch (status) {
    case 'completed':
      return 'success';
    case 'failed':
    case 'timed_out':
      return 'error';
    case 'running':
      return 'info';
    case 'pending':
    case 'paused':
    case 'waiting_approval':
      return 'warning';
    default:
      return 'default';
  }
}

/** Formats an ISO date string into a readable format. */
function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function ExecutionCompare() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const leftId = searchParams.get('left') ?? '';
  const rightId = searchParams.get('right') ?? '';
  const hasBothIds = !!leftId && !!rightId;

  // Fetch both executions (only when IDs are present)
  const {
    data: leftExecution,
    isLoading: leftLoading,
    isError: leftError,
    error: leftErrorObj,
  } = useExecution(leftId);

  const {
    data: rightExecution,
    isLoading: rightLoading,
    isError: rightError,
    error: rightErrorObj,
  } = useExecution(rightId);

  // --- No IDs yet: show selector ---
  if (!hasBothIds) {
    return (
      <CompareSelector
        initialLeft={leftId || undefined}
        initialRight={rightId || undefined}
      />
    );
  }

  // --- Loading state ---
  if (leftLoading || rightLoading) {
    return (
      <div className="flex justify-center py-20">
        <LoadingSpinner size="lg" label="Loading executions for comparison..." />
      </div>
    );
  }

  // --- Error state ---
  if (leftError || rightError) {
    const errorMsg =
      (leftErrorObj as Error)?.message ??
      (rightErrorObj as Error)?.message ??
      'Failed to fetch one or both executions.';

    return (
      <EmptyState
        icon={<GitCompareArrows className="h-12 w-12" />}
        message="Comparison Failed"
        description={errorMsg}
        action={
          <Button
            variant="secondary"
            onClick={() => navigate(ROUTE_PATHS.EXECUTION_COMPARE)}
            icon={<ArrowLeft className="h-4 w-4" />}
          >
            Choose Different Executions
          </Button>
        }
      />
    );
  }

  // --- Data ready ---
  if (!leftExecution || !rightExecution) {
    return (
      <EmptyState
        icon={<GitCompareArrows className="h-12 w-12" />}
        message="Executions Not Found"
        description="One or both executions could not be found. They may have been deleted."
        action={
          <Button
            variant="secondary"
            onClick={() => navigate(ROUTE_PATHS.EXECUTION_COMPARE)}
            icon={<ArrowLeft className="h-4 w-4" />}
          >
            Choose Different Executions
          </Button>
        }
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(ROUTE_PATHS.EXECUTION_COMPARE)}
          icon={<ArrowLeft className="h-4 w-4" />}
        >
          Change Selection
        </Button>
      </div>

      {/* Side-by-side execution headers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ExecutionHeader execution={leftExecution} label="Left" />
        <ExecutionHeader execution={rightExecution} label="Right" />
      </div>

      {/* Diff view */}
      <CompareDiffView left={leftExecution} right={rightExecution} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Execution Header Card
// ---------------------------------------------------------------------------

/** Displays a summary header card for one side of the comparison. */
function ExecutionHeader({
  execution,
  label,
}: {
  execution: Execution;
  label: 'Left' | 'Right';
}) {
  const navigate = useNavigate();
  const accentBorder =
    label === 'Left' ? 'border-l-blue-500' : 'border-l-trunnion-gold-500';

  return (
    <Card padding="none" className={`border-l-2 ${accentBorder}`}>
      <div className="p-4">
        {/* Top row: label + link */}
        <div className="flex items-center justify-between mb-2">
          <span
            className={`text-[10px] font-bold uppercase tracking-widest ${
              label === 'Left' ? 'text-blue-400' : 'text-trunnion-gold-500'
            }`}
          >
            {label} Execution
          </span>
          <button
            onClick={() => navigate(ROUTE_PATHS.EXECUTION_DETAIL(execution.id))}
            className="inline-flex items-center gap-1 text-xs text-trunnion-slate-400 hover:text-white transition-colors"
            title="Open execution detail"
          >
            <ExternalLink className="h-3 w-3" />
            View
          </button>
        </div>

        {/* ID + status */}
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <span className="font-mono text-sm text-white" title={execution.id}>
            {execution.id.slice(0, 8)}...
          </span>
          <Badge variant={statusVariant(execution.status)} dot>
            {EXECUTION_STATUS_CONFIG[execution.status]?.label ?? execution.status}
          </Badge>
          <ClassificationTag level={execution.classification} size="sm" />
        </div>

        {/* Metadata row */}
        <div className="flex items-center gap-3 text-xs text-trunnion-slate-400 flex-wrap">
          <span>{execution.workflowName}</span>
          <span className="text-trunnion-slate-600">|</span>
          <span>{formatDate(execution.startedAt)}</span>
          <span className="text-trunnion-slate-600">|</span>
          <span>{execution.totalTokensUsed.toLocaleString()} tokens</span>
          <span className="text-trunnion-slate-600">|</span>
          <span>{execution.steps.length} steps</span>
        </div>
      </div>
    </Card>
  );
}
